<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { BuildingOffice2Icon, PlusIcon } from '@heroicons/vue/24/outline'
import { apiErrorMessage, createPhysicalStation, getPhysicalStations } from '../api.js'
import { useI18n } from '../i18n.js'
import { hasPermission } from '../session.js'

const { t } = useI18n()
const stations = ref([])
const loading = ref(true)
const saving = ref(false)
const createOpen = ref(false)
const error = ref('')
const canCreate = computed(() => hasPermission('stations.configuration.create'))
const form = reactive({ stableCode: '', displayName: '', address: '', timezone: 'Europe/Kyiv', status: 'ACTIVE', note: '' })

async function load() {
  loading.value = true
  error.value = ''
  try {
    stations.value = (await getPhysicalStations()).stations ?? []
  } catch (cause) {
    error.value = apiErrorMessage(t, cause)
  } finally {
    loading.value = false
  }
}

async function createStation() {
  saving.value = true
  error.value = ''
  try {
    const station = await createPhysicalStation({ ...form })
    stations.value = [...stations.value, station].sort((a, b) => a.displayName.localeCompare(b.displayName))
    Object.assign(form, { stableCode: '', displayName: '', address: '', timezone: 'Europe/Kyiv', status: 'ACTIVE', note: '' })
    createOpen.value = false
  } catch (cause) {
    error.value = apiErrorMessage(t, cause, 'platformStationSaveFailed')
  } finally {
    saving.value = false
  }
}

onMounted(load)
</script>

<template>
  <section class="platform-page platform-stations-page">
    <header class="platform-page-heading">
      <div><p class="eyebrow">{{ t('administration') }}</p><h1>{{ t('physicalStations') }}</h1><p>{{ t('physicalStationsHint') }}</p></div>
      <button v-if="canCreate" class="button platform-primary" type="button" @click="createOpen = !createOpen">
        <PlusIcon aria-hidden="true" /> {{ t('addPhysicalStation') }}
      </button>
    </header>
    <p v-if="error" class="notice error" role="alert">{{ error }}</p>

    <form v-if="createOpen" class="platform-form-card" @submit.prevent="createStation">
      <header><h2>{{ t('newPhysicalStation') }}</h2><button class="text-action" type="button" @click="createOpen = false">{{ t('cancel') }}</button></header>
      <div class="platform-form-grid">
        <label>{{ t('stationStableCode') }}<input v-model.trim="form.stableCode" maxlength="64" required /></label>
        <label>{{ t('stationName') }}<input v-model.trim="form.displayName" maxlength="120" required /></label>
        <label class="wide">{{ t('address') }}<input v-model.trim="form.address" maxlength="500" /></label>
        <label>{{ t('timezone') }}<input v-model.trim="form.timezone" maxlength="64" required /></label>
        <label>{{ t('status') }}<select v-model="form.status"><option value="ACTIVE">{{ t('active') }}</option><option value="INACTIVE">{{ t('disabled') }}</option></select></label>
        <label class="wide">{{ t('note') }}<textarea v-model.trim="form.note" rows="3" maxlength="2000"></textarea></label>
      </div>
      <footer><button class="button platform-primary" :disabled="saving" type="submit">{{ saving ? t('saving') : t('create') }}</button></footer>
    </form>

    <div v-if="loading" class="platform-loading">{{ t('loading') }}</div>
    <div v-else-if="stations.length" class="station-registry-grid">
      <RouterLink v-for="station in stations" :key="station.id" class="station-registry-card" :to="`/platform/stations/${station.id}`">
        <span class="station-registry-icon"><BuildingOffice2Icon aria-hidden="true" /></span>
        <span><small>{{ station.stableCode }}</small><strong>{{ station.displayName }}</strong><em>{{ station.address || t('addressNotSpecified') }}</em></span>
        <b class="status-pill" :class="station.status.toLowerCase()">{{ station.status === 'ACTIVE' ? t('active') : t('disabled') }}</b>
      </RouterLink>
    </div>
    <div v-else class="catalog-empty-state"><BuildingOffice2Icon class="catalog-empty-icon" aria-hidden="true" /><strong>{{ t('noPhysicalStations') }}</strong><p>{{ t('noPhysicalStationsHint') }}</p></div>
  </section>
</template>
